/**
 * The console's top bar.
 *
 *   [NexERP Admin] | [● All services healthy]    [ Search… Ctrl K ]    [+] [bell] [theme] [you]
 *
 * No organization switcher, because a platform administrator works ACROSS
 * organizations rather than inside one — the search finds any of them
 * instead. The live platform-health indicator stays in the bar, where it has
 * always been, since "is anything down" is the question this audience asks
 * most often. On a small screen it condenses to a dot, and the tab bar at the
 * bottom takes over the rest.
 */
import { Link } from 'react-router-dom';
import { Icon } from '../components/Icon.jsx';
import { useAppearance } from '../hooks/useAppearance.js';
import { Brand } from './Brand.jsx';
import { NotificationCenter } from './NotificationCenter.jsx';
import { QuickCreate } from './QuickCreate.jsx';
import { UserMenu } from './UserMenu.jsx';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');
export const MOD_KEY = isMac ? '⌘' : 'Ctrl';

/** The platform's state in a few words, linking to the detail. */
function HealthStatus({ health }) {
  const status = health?.status;
  const tone =
    status === 'healthy' ? 'success' : status === 'degraded' ? 'warning' : status ? 'danger' : 'neutral';

  const label =
    status === 'healthy'
      ? 'All services healthy'
      : status === 'degraded'
        ? `${health?.degradedServices?.length ?? 0} degraded`
        : status === 'unavailable'
          ? `${health?.unreachableServices?.length ?? 0} unreachable`
          : 'Checking…';

  return (
    <Link to="/health" className={`topbar-health topbar-health-${tone}`} title={`Service health: ${label}`}>
      <span className="topbar-health-dot" aria-hidden="true" />
      <span className="topbar-health-label">{label}</span>
    </Link>
  );
}

function ThemeToggle() {
  const { resolved, setTheme } = useAppearance();
  const next = resolved === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      className="topbar-icon-btn topbar-theme"
      onClick={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setTheme(next, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      }}
      aria-label={`Switch to ${next} theme`}
      title={`Switch to ${next} theme`}
    >
      <Icon name={resolved === 'dark' ? 'sun' : 'moon'} size={17} />
    </button>
  );
}

export function Topbar({ onOpenCommand, onOpenNav, health, moduleRequests, onShowShortcuts }) {
  return (
    <header className="topbar">
      <div className="topbar-left">
        <button type="button" className="topbar-icon-btn topbar-menu" onClick={onOpenNav} aria-label="Console navigation">
          <Icon name="menu" size={18} />
        </button>

        <Link to="/" className="topbar-brand" aria-label="NexERP Admin — overview" title="Overview">
          <Brand />
        </Link>

        <span className="topbar-divider" aria-hidden="true" />
        <HealthStatus health={health} />
      </div>

      <button
        type="button"
        className="topbar-search"
        onClick={onOpenCommand}
        aria-label="Search the console"
        aria-keyshortcuts="Control+K Meta+K"
      >
        <Icon name="search" size={15} />
        <span className="topbar-search-text">Search organizations, pages, actions…</span>
        <span className="topbar-search-keys" aria-hidden="true">
          <kbd>{MOD_KEY}</kbd>
          <kbd>K</kbd>
        </span>
      </button>

      <div className="topbar-right">
        <button type="button" className="topbar-icon-btn topbar-search-icon" onClick={onOpenCommand} aria-label="Search">
          <Icon name="search" size={17} />
        </button>
        <QuickCreate />
        <NotificationCenter moduleRequests={moduleRequests} health={health} />
        <ThemeToggle />
        <UserMenu onShowShortcuts={onShowShortcuts} />
      </div>
    </header>
  );
}
